/**
 * Atribuição da landing page — de qual anúncio / origem / produto o visitante veio,
 * e quem é este visitante entre uma visita e outra.
 *
 * Por que existe:
 *  - os parâmetros (?ad=, ?utm=, ?ref=, ?pxl=, ?produto=) só existem no PRIMEIRO
 *    carregamento; quem clicava no anúncio, saía e voltava pelo endereço direto
 *    chegava sem nada e o lead nascia órfão;
 *  - `URLSearchParams.get()` devolve `null` para chave ausente, e o App.vue gravava
 *    esse `null` por cima do que já estava salvo — a persistência já existia no
 *    `vuex-persistedstate` e era destruída a cada carga;
 *  - sem um id de visitante, cada aba virava uma sessão solta e não havia como
 *    montar o histórico de visitas na ficha do lead.
 *
 * Este módulo é o DONO da persistência da atribuição. O Vuex guarda apenas o valor
 * resolvido da carga atual (`urlParams`) — por isso `urlParams` saiu do
 * `vuex-persistedstate`: com dois donos, o balde que não expira manteria vivo um
 * anúncio que o outro já venceu.
 *
 * Tudo é best-effort: Safari privado lança em `localStorage.setItem`, então cada
 * leitura/escrita é isolada e o pior caso é a LP se comportar como antes.
 */

const KEY_ULTIMO    = 'zm-attr';
const KEY_PRIMEIRO  = 'zm-attr-first';
const KEY_VISITANTE = 'zm-visitante';

const DIA = 24 * 60 * 60 * 1000;

// Último toque: janela de clique paga. Primeiro toque: janela de campanha (padrão GA4).
const TTL_ULTIMO    = 30 * DIA;
const TTL_PRIMEIRO  = 90 * DIA;
const TTL_VISITANTE = 90 * DIA;

/**
 * Chaves capturadas da URL.
 *
 * `utm` é o token único do link da bio ("instagram"), que já existe no banco e
 * convive com os `utm_*` padrão — que Meta e Google anexam sozinhos e que até
 * agora eram simplesmente descartados.
 */
const CHAVES = [
  'ad', 'utm', 'ref', 'pxl', 'produto',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'gclid', 'fbclid',
];

const CHAVES_UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];

// ── Armazenamento tolerante a falha ──────────────────────────────────────────

function cookieLer(chave) {
  try {
    const alvo = `${encodeURIComponent(chave)}=`;
    const achado = document.cookie.split('; ').find(c => c.indexOf(alvo) === 0);
    return achado ? decodeURIComponent(achado.slice(alvo.length)) : null;
  } catch (e) {
    return null;
  }
}

function cookieGravar(chave, valor) {
  try {
    // Cookie de sessão: é o fallback de quando o localStorage não existe
    // (Safari privado). Não é a estratégia principal — cookie escrito por JS
    // é capado em 7 dias pelo ITP, o que seria pior que o localStorage.
    document.cookie = `${encodeURIComponent(chave)}=${encodeURIComponent(valor)}; path=/; SameSite=Lax`;
  } catch (e) {
    // Sem onde guardar — a LP segue funcionando sem memória de atribuição.
  }
}

function lerJson(chave) {
  let bruto = null;

  try {
    bruto = localStorage.getItem(chave);
  } catch (e) {
    bruto = null;
  }

  if (!bruto) {
    bruto = cookieLer(chave);
  }

  if (!bruto) return null;

  try {
    const valor = JSON.parse(bruto);
    return valor && typeof valor === 'object' ? valor : null;
  } catch (e) {
    return null;
  }
}

function gravarJson(chave, valor) {
  const bruto = JSON.stringify(valor);

  try {
    localStorage.setItem(chave, bruto);
    return;
  } catch (e) {
    cookieGravar(chave, bruto);
  }
}

function remover(chave) {
  try {
    localStorage.removeItem(chave);
  } catch (e) {
    // ignora
  }

  try {
    document.cookie = `${encodeURIComponent(chave)}=; path=/; Max-Age=0; SameSite=Lax`;
  } catch (e) {
    // ignora
  }
}

// ── Utilidades ───────────────────────────────────────────────────────────────

function gerarUuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function venceu(timestamp, ttl) {
  if (!timestamp) return true;
  return (Date.now() - timestamp) > ttl;
}

function somenteChavesConhecidas(objeto) {
  const limpo = {};

  CHAVES.forEach((chave) => {
    const valor = objeto[chave];
    if (valor !== null && valor !== undefined && valor !== '') {
      limpo[chave] = String(valor).slice(0, 200);
    }
  });

  return limpo;
}

/**
 * Parâmetros PRESENTES na URL. `has()` e não `get()`: chave ausente não entra no
 * payload, então nunca apaga o que já estava salvo. É a raiz do bug original.
 */
function daUrl() {
  const encontrados = {};

  try {
    const busca = new URLSearchParams(window.location.search);

    CHAVES.forEach((chave) => {
      if (busca.has(chave)) {
        const valor = (busca.get(chave) || '').trim();
        if (valor !== '') {
          encontrados[chave] = valor.slice(0, 200);
        }
      }
    });
  } catch (e) {
    // URL ilegível — segue sem parâmetros.
  }

  return encontrados;
}

/**
 * Origem deduzida do referrer, para quem chega sem parâmetro nenhum e sem nada
 * salvo. Hoje esse tráfego todo cai em 'landingpage' ou null.
 *
 * Os valores seguem a taxonomia que já existe no banco ('instagram').
 */
function doReferrer() {
  let host = '';

  try {
    if (!document.referrer) return null;

    host = new URL(document.referrer).hostname.toLowerCase();

    // Navegação interna da própria SPA não é origem.
    if (host === window.location.hostname) return null;
  } catch (e) {
    return null;
  }

  if (host.indexOf('instagram.') !== -1) return 'instagram';
  if (host.indexOf('facebook.') !== -1 || host.indexOf('fb.') === 0) return 'facebook';
  if (host.indexOf('google.') !== -1) return 'organico_google';
  if (host.indexOf('bing.') !== -1) return 'organico_bing';
  if (host.indexOf('youtube.') !== -1) return 'youtube';
  if (host.indexOf('linkedin.') !== -1) return 'linkedin';
  if (host.indexOf('tiktok.') !== -1) return 'tiktok';
  if (host.indexOf('whatsapp') !== -1) return 'whatsapp';

  return `ref:${host}`.slice(0, 60);
}

// ── Visitante ────────────────────────────────────────────────────────────────

/**
 * Id do navegador, estável entre visitas (ao contrário do id de sessão, que vive
 * em sessionStorage e nasce de novo a cada aba). É ele que transforma "a visita"
 * em "histórico de visitas" na ficha do lead.
 */
export function visitanteUuid() {
  const salvo = lerJson(KEY_VISITANTE);

  if (salvo && salvo.uuid && !venceu(salvo.visto_em, TTL_VISITANTE)) {
    // Renova a validade a cada visita — visitante ativo não expira.
    gravarJson(KEY_VISITANTE, { uuid: salvo.uuid, visto_em: Date.now() });
    return salvo.uuid;
  }

  const uuid = gerarUuid();
  gravarJson(KEY_VISITANTE, { uuid, visto_em: Date.now() });

  return uuid;
}

// ── Resolução ────────────────────────────────────────────────────────────────

function montarOrigem(params, origemReferrer) {
  return params.utm || params.utm_source || origemReferrer || null;
}

function montarUtmParams(params) {
  const utm = {};

  CHAVES_UTM.forEach((chave) => {
    if (params[chave]) utm[chave] = params[chave];
  });

  return Object.keys(utm).length ? utm : null;
}

function montarResultado(params, fonte, capturadoEm, primeiro, origemReferrer) {
  const resolvido = {
    ad:      params.ad      || null,
    utm:     params.utm     || null,
    ref:     params.ref     || null,
    pxl:     params.pxl     || null,
    produto: params.produto || null,

    origem:       montarOrigem(params, origemReferrer),
    utm_params:   montarUtmParams(params),
    fonte,
    capturado_em: capturadoEm || null,

    visitante_uuid: visitanteUuid(),

    primeiro: primeiro ? {
      ad:           primeiro.params && primeiro.params.ad ? primeiro.params.ad : null,
      origem:       primeiro.origem || null,
      fonte:        primeiro.fonte || null,
      capturado_em: primeiro.capturado_em || null,
    } : null,
  };

  return resolvido;
}

/**
 * Resolve a atribuição desta carga, nesta ordem:
 *   1. parâmetros presentes na URL          -> fonte 'url'
 *   2. o que está salvo e ainda não venceu  -> fonte 'storage'
 *   3. document.referrer                    -> fonte 'referrer'
 *   4. nada                                 -> fonte 'direto'
 *
 * A `fonte` viaja junto de propósito: sem ela, atribuição resgatada do storage
 * fica indistinguível de clique real no anúncio e o desempenho da campanha
 * melhora sozinho no relatório.
 */
export function resolver() {
  const naUrl = daUrl();
  const temNaUrl = Object.keys(naUrl).length > 0;

  const salvo = lerJson(KEY_ULTIMO);
  const salvoValido = salvo && !venceu(salvo.capturado_em, TTL_ULTIMO) ? salvo : null;

  if (!salvoValido && salvo) {
    remover(KEY_ULTIMO);
  }

  let primeiro = lerJson(KEY_PRIMEIRO);
  if (primeiro && venceu(primeiro.capturado_em, TTL_PRIMEIRO)) {
    remover(KEY_PRIMEIRO);
    primeiro = null;
  }

  // 1. URL manda — e só as chaves presentes entram, por cima do que já havia.
  if (temNaUrl) {
    const params = Object.assign({}, salvoValido ? salvoValido.params : {}, naUrl);
    const origemReferrer = doReferrer();

    // A `fonte` fala do anúncio: quem volta pelo link da bio (?utm=instagram) traz
    // parâmetro na URL, mas o anúncio veio da visita anterior.
    const fonte = !naUrl.ad && params.ad ? 'storage' : 'url';

    const registro = {
      params,
      origem: montarOrigem(params, origemReferrer),
      fonte,
      capturado_em: Date.now(),
    };

    gravarJson(KEY_ULTIMO, registro);

    if (!primeiro) {
      primeiro = registro;
      gravarJson(KEY_PRIMEIRO, registro);
    }

    return montarResultado(params, fonte, registro.capturado_em, primeiro, origemReferrer);
  }

  // 2. Nada na URL, mas há atribuição salva e válida — é o resgate.
  if (salvoValido) {
    return montarResultado(
      salvoValido.params || {},
      'storage',
      salvoValido.capturado_em,
      primeiro,
      salvoValido.origem || null,
    );
  }

  // 3. Primeira visita sem parâmetro: o referrer é o que existe.
  const origemReferrer = doReferrer();

  if (origemReferrer) {
    const registro = {
      params: {},
      origem: origemReferrer,
      fonte: 'referrer',
      capturado_em: Date.now(),
    };

    gravarJson(KEY_ULTIMO, registro);

    if (!primeiro) {
      primeiro = registro;
      gravarJson(KEY_PRIMEIRO, registro);
    }

    return montarResultado({}, 'referrer', registro.capturado_em, primeiro, origemReferrer);
  }

  // 4. Direto.
  return montarResultado({}, 'direto', null, primeiro, null);
}

/**
 * Registra parâmetros que não vieram da URL da carga inicial.
 *
 * Existe por causa da navegação interna: os CTAs fazem `router.push({ path })` e
 * derrubam a query, e o `/tablet` propaga `?produto=tablet` só na rota. Sem isso,
 * um F5 no `/whatsapp` devolvia o quiz genérico.
 */
export function registrar(parciais) {
  const limpo = somenteChavesConhecidas(parciais || {});

  if (!Object.keys(limpo).length) {
    return resolver();
  }

  const salvo = lerJson(KEY_ULTIMO);
  const salvoValido = salvo && !venceu(salvo.capturado_em, TTL_ULTIMO) ? salvo : null;

  const params = Object.assign({}, salvoValido ? salvoValido.params : {}, limpo);
  const registro = {
    params,
    origem: montarOrigem(params, salvoValido ? salvoValido.origem : null),
    fonte: salvoValido ? salvoValido.fonte : 'url',
    capturado_em: salvoValido ? salvoValido.capturado_em : Date.now(),
  };

  gravarJson(KEY_ULTIMO, registro);

  let primeiro = lerJson(KEY_PRIMEIRO);
  if (primeiro && venceu(primeiro.capturado_em, TTL_PRIMEIRO)) {
    primeiro = null;
  }

  if (!primeiro) {
    gravarJson(KEY_PRIMEIRO, registro);
    primeiro = registro;
  }

  return montarResultado(params, registro.fonte, registro.capturado_em, primeiro, registro.origem);
}

/**
 * Cookies do pixel da Meta para a Conversions API: `_fbp` identifica o navegador e
 * `_fbc` o clique no anúncio. O `_fbc` é gravado pelo próprio pixel quando a URL traz
 * `fbclid`; se não existir, é montado com o `fbclid` que a atribuição guardou, no
 * formato da Meta (fb.1.<quando>.<fbclid>).
 */
export function cookiesMeta() {
  const fbp = cookieLer('_fbp');
  let fbc = cookieLer('_fbc');

  if (!fbc) {
    const salvo = lerJson(KEY_ULTIMO);
    const fbclid = salvo && salvo.params ? salvo.params.fbclid : null;

    if (fbclid) {
      fbc = `fb.1.${salvo.capturado_em || Date.now()}.${fbclid}`;
    }
  }

  return { fbp: fbp || null, fbc: fbc || null };
}

/**
 * Esquece a atribuição. O id do visitante NÃO é apagado: ele identifica o
 * navegador, não a campanha.
 */
export function limpar() {
  remover(KEY_ULTIMO);
  remover(KEY_PRIMEIRO);
}
